/**
 * TavernCheers — centred celebration overlay for the "Tournée" action.
 * Two beer mugs animate toward each other and clink, with foam droplets
 * bursting outward. Lasts ~1.2 s then auto-dismisses (caller controls `show`).
 *
 * pointer-events-none so it never blocks clicks.
 * Motion is gated on prefers-reduced-motion (mug positions static, only fade).
 */
interface Props {
  show: boolean
}

export function TavernCheers({ show }: Props) {
  if (!show) return null

  return (
    <div
      role="status"
      aria-label="célébration"
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
    >
      {/* dim backdrop */}
      <div className="absolute inset-0 bg-[#04161c]/60 cheers-backdrop" />

      {/* clinking mugs + foam burst */}
      <div className="relative flex items-end justify-center gap-2">
        {/* left mug */}
        <svg
          viewBox="0 0 56 72"
          className="w-16 h-20 cheers-mug-left"
          aria-hidden="true"
          fill="none"
        >
          <MugShape side="left" />
        </svg>

        {/* foam droplets */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0">
          {DROPLETS.map((d, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-sand-100/90 cheers-droplet"
              style={{
                width: d.size,
                height: d.size,
                '--dx': d.dx,
                '--dy': d.dy,
                animationDelay: d.delay,
              } as React.CSSProperties}
            />
          ))}
        </div>

        {/* right mug */}
        <svg
          viewBox="0 0 56 72"
          className="w-16 h-20 cheers-mug-right"
          aria-hidden="true"
          fill="none"
        >
          <MugShape side="right" />
        </svg>
      </div>
    </div>
  )
}

// ── Mug SVG shape ─────────────────────────────────────────────────────────────

function MugShape({ side }: { side: 'left' | 'right' }) {
  // A simple tavern mug: body, handle, foam head.
  const flip = side === 'right' ? 'scale(-1,1) translate(-56,0)' : undefined
  return (
    <g transform={flip}>
      {/* body */}
      <path
        d="M8,20 L10,62 Q28,68 46,62 L48,20 Z"
        fill="#C9A55C"
        stroke="#7A3E1F"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* foam head */}
      <ellipse cx="28" cy="20" rx="20" ry="7" fill="#F5EDD7" />
      <ellipse cx="22" cy="16" rx="7" ry="5" fill="#F5EDD7" />
      <ellipse cx="34" cy="15" rx="6" ry="5" fill="#F5EDD7" />
      {/* handle */}
      <path
        d="M46,28 Q62,28 62,40 Q62,52 46,52"
        stroke="#7A3E1F"
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      {/* rim highlight */}
      <ellipse cx="28" cy="20" rx="20" ry="7" fill="none" stroke="#E5CC8F" strokeWidth="1" opacity="0.6" />
      {/* liquid shine */}
      <line x1="16" y1="32" x2="16" y2="55" stroke="#E5CC8F" strokeWidth="2" strokeLinecap="round" opacity="0.35" />
    </g>
  )
}

// ── Foam droplets ──────────────────────────────────────────────────────────────

const DROPLETS: Array<{ size: string; dx: string; dy: string; delay: string }> = [
  { size: '8px',  dx: '-48px', dy: '-56px', delay: '0ms'   },
  { size: '6px',  dx:  '40px', dy: '-60px', delay: '40ms'  },
  { size: '10px', dx: '-24px', dy: '-72px', delay: '20ms'  },
  { size: '6px',  dx:  '20px', dy: '-68px', delay: '60ms'  },
  { size: '5px',  dx: '-56px', dy: '-40px', delay: '80ms'  },
  { size: '5px',  dx:  '52px', dy: '-44px', delay: '50ms'  },
  { size: '7px',  dx:   '0px', dy: '-80px', delay: '10ms'  },
  { size: '4px',  dx: '-36px', dy: '-64px', delay: '90ms'  },
]
